import React, { useSyncExternalStore, useEffect, useMemo, useRef, useState } from 'react'
import * as THREE from 'three'
import { useThree, useFrame } from '@react-three/fiber'
import { OrbitControls } from '@react-three/drei'
import SplatEnvironment from './SplatEnvironment'
import EnvProbe from './EnvProbe'
import LightProbeVolume from './LightProbeVolume'
import ReflectionCaptureVolume from './ReflectionCaptureVolume'
import NavMeshVolume from './NavMeshVolume'
import WaypointPickerOverlay from './WaypointPickerOverlay'
import ViewportContextMenu from './ViewportContextMenu'
import WalkHereRoute from './WalkHereRoute'
import VoxelVolume from './VoxelVolume'
import AgentCapsuleGizmo from './AgentCapsuleGizmo'
import TransformGizmo from './TransformGizmo'
import ProbeReferenceSphere from './ProbeReferenceSphere'
import HairSplat from './HairSplat'
import { isCiMode } from '../lib/ciMode'
import { HDRIApplier } from './HDRISwitcher'
import ShadowSunLight from './ShadowSunLight'
import ShadowCatcher from './ShadowCatcher'
import TorchLight from './TorchLight'
import SpotlightRig from './SpotlightRig'
import { sphericalToXYZ } from '../lib/lightPlacement'
import { shouldShowShadowCatcher } from '../lib/shadowVisibility'
import { showStudioStage } from '../lib/studioStageVisibility'
import { useLiveTune, useCameraCageGizmoVisible } from '../hooks/useLiveTune'
import { useCameraRigState } from '../hooks/useCameraRigState'
import { liveTuneStore } from '../lib/liveTuneStore'
import { getContentTarget, subscribeContentTarget } from '../lib/viewportFraming'
import { cameraController } from '../lib/cameraController'
import { EDITOR_MOUSE_BUTTONS } from '../lib/editorCameraControls'
import { cameraRigCompat } from '../lib/cameraRigCompat'
import { CAMERA_ACCENT } from '../lib/cameraTheme'
import { input } from '../lib/inputComponent'
import { EventRuntime } from '../lib/EventRuntime'
import { movement } from '../lib/movementComponent'
import { avatarFloor } from '../lib/avatarFloor'
import { activeEnvAssetId } from '../lib/activeEnvironmentAsset'
import { getBakeGeneration, subscribeBakeReload } from '../lib/bakeReload'
import { kelvinToHex } from '../lib/colorTemperature'
import { setMaterialTuning } from '../lib/avatarMaterial'

// Reused per-frame by CameraControllerSync so the camera hot path allocates
// nothing (CLAUDE.md: no per-frame allocations on the render path).
const _camScratchPos = [0, 0, 0]
const _camScratchFwd = [0, 0, 1]
const _camScratchCtx = { stageFloorY: 0, headProbe: null, avatarPos: null, avatarForward: null }

/**
 * THREE.HemisphereLight shares ONE `intensity` uniform between skyColor and
 * groundColor (WebGLLights bakes light.intensity into both equally) — there's
 * no native per-direction intensity. Fake it by pre-multiplying each color by
 * its own intensity factor before handing it to the light; the light's own
 * `intensity` is left at the three.js default (1).
 */
function scaledColor(hex, intensity) {
  return new THREE.Color(hex).multiplyScalar(intensity)
}

/**
 * One shared debug/test handle for the backdrop (ENG-894). The sky and the
 * ground are separate mounts, so they each write their half into this object
 * rather than owning a global apiece. `scene` is exposed because neither half
 * is reachable by traversing window.__AVATAR_SCENE__ (the rig's own scene) —
 * same reason __CAMERA_CAGE_GIZMO__ / __SPOTLIGHT_TARGET__ exist.
 */
function backdropGlobal() {
  if (typeof window === 'undefined') return null
  window.__BACKDROP__ ??= { scene: null, skyColor: null, ground: null }
  return window.__BACKDROP__
}

/**
 * SceneBackdrop — drives THREE `scene.background` from the tune store.
 *
 * Only mounted while `backdrop.skyEnabled`, and it restores whatever the
 * background was on unmount, so the OFF state is genuinely "nobody owns
 * scene.background" (null → the themed CSS canvas background shows through)
 * rather than a hardcoded color pretending to be the default.
 */
function SceneBackdrop({ color }) {
  const scene = useThree(s => s.scene)
  useEffect(() => {
    const prev = scene.background
    const applied = new THREE.Color(color)
    scene.background = applied
    const g = backdropGlobal()
    if (g) { g.scene = scene; g.skyColor = color }
    return () => {
      // Only hand back the previous value if we're still the owner — a later
      // writer (an HDRI, a future env background) must not be clobbered.
      if (scene.background === applied) scene.background = prev
      const gg = backdropGlobal()
      if (gg && gg.skyColor === color) gg.skyColor = null
    }
  }, [scene, color])
  return null
}

/**
 * Local-asset hair mount — renders the shipped `public/assets/hair_static/*`
 * bundle directly, with no backend. Gated behind
 * `?hairlocal=1` so normal sessions don't pull ~184 MB + 4 ONNX nets (~40s).
 * Uses HairSplat's built-in Myra defaults (attach matrix, followBone='head',
 * assetsBaseUrl='/assets/hair_static'). Use this to author/record Myra's hair
 * locally, with no backend at all.
 */
function GSSHairLocalMount() {
  const on = React.useMemo(() => {
    if (typeof window === 'undefined') return false
    return new URLSearchParams(window.location.search).get('hairlocal') === '1'
  }, [])
  if (isCiMode()) return null
  if (!on) return null
  return <HairSplat assetsBaseUrl="/assets/hair_static" followBone="head" />
}

/**
 * CameraControllerSync — the single per-frame driver for the blueprint-owned
 * camera. Wires the live camera + OrbitControls into the
 * cameraController singleton and calls apply() every frame. This folds in what
 * used to be three separate components: FovSync, CameraCage, and the preset
 * property-writes. State lives in liveTuneStore (read directly in useFrame — no
 * React state on the hot path, CLAUDE.md hard rule).
 *
 * Mounted AFTER <OrbitControls> so drei's own useFrame runs first; the cage
 * clamp then re-syncs via controls.update() (mirrors the old CameraCage order).
 */
function CameraControllerSync({ stageFloorY, headProbe }) {
  const camera = useThree(s => s.camera)
  useEffect(() => {
    cameraController.setCamera(camera)
    cameraController.setControls(cameraRigCompat)
    // Adopt the camera R3F actually mounted (App.jsx's <Canvas camera={{position:[-0.5,1.4,6.5]}}>,
    // or wherever edit mode left it) into the rig. Without this the first frame would snap
    // the camera to the rig's default pose on every live↔edit flip.
    // Aim at the loaded content. With nothing framed yet the rig keeps whatever
    // target it has — aiming at the origin would point it at the floor.
    const aim = getContentTarget()
    if (aim) {
      cameraRigCompat.target.set(aim[0], aim[1], aim[2])
      cameraController.setRigTarget(aim[0], aim[1], aim[2])
    }
    cameraController.syncFromCamera()
  }, [camera]) // eslint-disable-line react-hooks/exhaustive-deps
  useFrame((_, dt) => {
    // Follow mode needs the live avatar world pose (headProbe is fixed at x/z=0).
    // Read the same group the getCharacter* getters do (movementComponent);
    // null until the avatar mounts → controller falls back to the head probe.
    // Fill reused scratch (no per-frame alloc — CLAUDE.md hard rule).
    const ctx = _camScratchCtx
    ctx.stageFloorY = stageFloorY
    ctx.headProbe = headProbe
    const g = movement.getGroup()
    if (g) {
      const p = g.position, yaw = g.rotation.y
      _camScratchPos[0] = p.x; _camScratchPos[1] = p.y; _camScratchPos[2] = p.z
      _camScratchFwd[0] = Math.sin(yaw); _camScratchFwd[1] = 0; _camScratchFwd[2] = Math.cos(yaw)  // +Z rest-forward
      ctx.avatarPos = _camScratchPos
      ctx.avatarForward = _camScratchFwd
    } else {
      ctx.avatarPos = null
      ctx.avatarForward = null
    }
    cameraController.apply(dt, ctx)
  })
  return null
}

/**
 * EditorHeadParallax — the head-tracked parallax stage (ENG-981) for the two
 * views apply() does NOT run in: edit mode and live's rig-less fallback,
 * where drei's OrbitControls owns the camera.
 *
 * TWO passes bracketing drei's update (which runs at useFrame priority -1),
 * and the bracket is load-bearing: OrbitControls.update() re-derives its
 * orbit state from camera.position, ADOPTING whatever it finds — a bare
 * additive offset gets adopted as the new base every frame and re-added,
 * compounding into a spiral (found live: first click of the rail button spun
 * the camera). So -2 strips exactly what was added last frame, drei adopts
 * the clean base, then the default-priority pass composes the fresh offset
 * (translation + swivel + off-axis skew) on top. Full rationale on
 * cameraController.applyHeadOffsetTo. Mutually exclusive with
 * CameraControllerSync (the rigDriven split), so the offset always has
 * exactly one applier; smoothing state is shared, so a live↔edit flip
 * mid-session stays continuous. With tracking off both passes are a few
 * comparisons, no allocation.
 */
function EditorHeadParallax({ controlsRef }) {
  useFrame((state) => {
    cameraController.unapplyHeadOffsetFrom(state.camera, controlsRef.current)
  }, -2)
  useFrame((state, dt) => {
    const ctl = controlsRef.current
    if (!ctl) return
    cameraController.applyHeadOffsetTo(state.camera, ctl, dt)
  })
  return null
}

/**
 * ViewportInput — attaches the inputComponent singleton to the WebGL canvas and drives its
 * per-frame dispatch. Live mode only, mirroring CameraControllerSync: in edit mode drei's
 * OrbitControls owns input, so attaching here too would give the editor camera two drivers.
 *
 * consumeFrame() runs BEFORE cameraController.apply() (this is mounted before
 * CameraControllerSync, and R3F runs useFrame callbacks in mount order), so input the user
 * generated this frame reaches the rig in the same frame rather than one frame late.
 */
function ViewportInput() {
  const gl = useThree(s => s.gl)
  useEffect(() => {
    const el = gl?.domElement
    if (!el) return
    input.setEventSink((type, data) => EventRuntime.fireEvent(type, data))
    input.attach(el, window)
    return () => input.detach()
  }, [gl])
  useFrame(() => { input.consumeFrame() })
  return null
}

/**
 * CameraCageGizmo — dev-only wireframe box visualising the blueprint camera's
 * cage (liveTuneStore camera.cage: half-extents sized around a center that is
 * a STABLE anchor — the avatar's own world position, not the freely-pannable
 * orbit target — PLUS the cage's own ox/oy/oz offset; 0 offset centers it on
 * the avatar). Deliberately not anchored to the orbit target: a pan
 * would otherwise drag the whole box along with it, defeating "place the
 * box somewhere and leave it there". Mirrors cameraController.apply()'s
 * cage-clamp anchor exactly (same avatar-else-target fallback), so what's
 * drawn is what's enforced. Gated on the ephemeral `cageGizmoVisible` flag
 * (never persisted, never a URL/blueprint value — see
 * liveTuneStore.setCageGizmoVisible).
 *
 * One Box3Helper, created once via useMemo; mutated imperatively in useFrame
 * (never React state, never reallocated per frame — CLAUDE.md hard rule).
 * Exposed as window.__CAMERA_CAGE_GIZMO__ for tests/console: Scene.jsx's
 * contents are siblings of AvatarModel's `combined` group under the Canvas
 * root (window.__AVATAR_SCENE__), not descendants of it, so scene traversal
 * from that global can't reach anything Scene.jsx mounts — this mirrors the
 * spotTarget precedent above (window.__SPOTLIGHT_TARGET__).
 */
function CameraCageGizmo({ headProbe }) {
  const visible = useCameraCageGizmoVisible()
  const helper = useMemo(() => {
    const h = new THREE.Box3Helper(new THREE.Box3(), new THREE.Color(CAMERA_ACCENT))
    h.name = 'cameraCageGizmo'
    return h
  }, [])
  useEffect(() => {
    if (typeof window !== 'undefined') window.__CAMERA_CAGE_GIZMO__ = helper
  }, [helper])
  useFrame(() => {
    if (!visible) return
    const cage = liveTuneStore.getState().camera.cage
    // Anchor: the avatar's live world position when it's mounted (same
    // source cameraController.apply() reads via ctx.avatarPos — no per-frame
    // allocation, movement.getGroup() returns the live group), else the
    // OrbitControls target as a fallback for an avatar-less scene. Reading
    // the target here (not via getTransform(), which allocates) is fine only
    // as the fallback path since it's cheap and this is a dev-only gizmo.
    const g = movement.getGroup()
    let ax, ay, az
    if (g) {
      ax = g.position.x; az = g.position.z
      ay = headProbe ? headProbe[1] : g.position.y
    } else {
      const target = window.__ORBIT_CONTROLS__?.target
      ax = target?.x ?? 0; ay = target?.y ?? 1; az = target?.z ?? 0 // fallback if controls not ready
    }
    const cx = ax + (cage.ox ?? 0), cy = ay + (cage.oy ?? 0), cz = az + (cage.oz ?? 0)
    helper.box.min.set(cx - cage.hx, cy - cage.hy, cz - cage.hz)
    helper.box.max.set(cx + cage.hx, cy + cage.hy, cz + cage.hz)
  })
  if (!visible) return null
  return <primitive object={helper} />
}

/**
 * Live-mirror the splat's pose rotation onto scene.environmentRotation
 * so the IBL (and mirror-ball reflection) stays locked to the visible
 * environment when the user rotates the splat. SKIN_MATERIAL picks this
 * up automatically via three's envMap sampling pipeline.
 */
function EnvRotationSync({ rx, ry, rz }) {
  const scene = useThree(s => s.scene)
  useEffect(() => {
    if (!scene) return
    if (!scene.environmentRotation) scene.environmentRotation = new THREE.Euler()
    scene.environmentRotation.set(rx, ry, rz)
  }, [scene, rx, ry, rz])
  return null
}

/**
 * Keep the camera framed on the avatar when the environment's floor isn't at
 * world 0. OrbitControls' target tracks the stage floor via its prop; this
 * shifts the camera by the same delta so the view angle + distance are
 * preserved — a splat whose floor sits at y=-6 just drops the whole rig down
 * 6 m. No-op when the floor is 0 (studio/Messy, edit mode), and it restores
 * the shift when the floor returns to 0 (env removed / live→edit).
 */
function CameraFloorSync({ controlsRef, floorY, live }) {
  const camera = useThree(s => s.camera)
  const appliedRef = useRef(0)
  useEffect(() => {
    // LIVE: the rig recomputes camera.position from {target,yaw,pitch,dist} every frame, so a
    // raw camera.y nudge would be silently overwritten next frame. Shift the rig's TARGET
    // instead and let the pose follow — same visual result, expressed in the rig's terms.
    // (This component is a third camera writer that predates the rig and ran in both modes
    // off controlsRef, which in live mode is now null anyway — its update() would no-op.)
    if (live) {
      const next = Number.isFinite(floorY) ? floorY : 0
      const delta = next - appliedRef.current
      if (Math.abs(delta) < 1e-6) return
      const t = cameraController.getRig().target
      cameraController.setRigTarget(t[0], t[1] + delta, t[2])
      appliedRef.current = next
      return
    }
    // Keep a single floor-delta baked into camera.y, independent of preset. Both
    // presets place the camera floorY metres up: orbit relies on this shift to
    // preserve framing when the floor moves, and thirdPerson's absolute pose
    // (cameraController._applyPreset) is itself 1.7 + floorY. So the offset
    // actually present in camera.y is floorY in either preset. (An earlier
    // version reset this to 0 for thirdPerson, which double-added floorY on an
    // orbit→thirdPerson→orbit round-trip over a floored environment.)
    const next = Number.isFinite(floorY) ? floorY : 0
    const delta = next - appliedRef.current
    if (Math.abs(delta) < 1e-6) return
    camera.position.y += delta
    appliedRef.current = next
    controlsRef.current?.update()
  }, [camera, controlsRef, floorY, live])
  return null
}

const AVATAR_HEAD_PROBE = [0, 1.4, 0]

// Wave 2B "one mode": the `live` prop is gone — the viewport is ALWAYS the
// playing scene (blueprint rig camera, environment stack, viewport input)
// while staying editable. What used to fork on `live` now forks, where a fork
// is still needed at all, on whether an ENVIRONMENT is active (`hasEnv`):
// studio authoring lighting + grid when there's no environment, the env's own
// IBL/splat stack when there is one.
export default function Scene({ splatUrl, streamChar = null, navmeshSelected = false }) {
  const controlsRef = useRef()
  const [iblApplied, setIblApplied] = useState(false)
  const [floorY, setFloorY] = useState(null)
  const tune = useLiveTune()

  // Does the live blueprint actually contain a camera rig? Drives camera
  // OWNERSHIP: a rig in the blueprint hands the camera to it
  // (CameraControllerSync + cameraRigCompat); a rig-less blueprint gets the
  // editor OrbitControls, so it is navigable instead of frozen. See the
  // camera mounts below.
  const rigState = useCameraRigState(true)
  const rigDriven = rigState === 'ok'

  // Splat URL — the tune store owns it (LiveTunePanel / applyEnvironment /
  // the ?splat= URL param all write there via parseTuneUrlParams). The prop
  // is a last-resort fallback for callers that pass one directly.
  const activeSplatUrl = tune.environment.splatUrl ?? splatUrl ?? null
  // An active environment flips the viewport from "studio authoring stage"
  // (fixed em_* lights + grid floor) to the environment's own stack (splat +
  // IBL + probes). This is the fork `live` used to approximate.
  const hasEnv = !!activeSplatUrl
  // The studio floor + reference grid. Both halves of the backdrop stand it
  // down, for two different reasons — see studioStageVisibility.js.
  const studioStage = showStudioStage({
    hasEnv,
    skyEnabled: tune.backdrop.skyEnabled,
    groundEnabled: tune.backdrop.groundEnabled,
  })

  // Baked-artifact reload signal (see src/lib/bakeReload.js). When a bake for the
  // ACTIVE environment completes, its generation bumps; folding that into a
  // reloadKey the artifact volumes carry in their fetch-effect deps makes them
  // re-fetch the freshly-written navmesh/lightprobes/reflections/collision —
  // instead of leaving the row reading NOT BUILT until a full page reload. Keyed
  // by the active env's asset id, so a bake finishing on another env is ignored.
  const [, bumpReload] = useState(0)
  useEffect(() => subscribeBakeReload(() => bumpReload(n => n + 1)), [])
  const bakeReloadKey = getBakeGeneration(activeEnvAssetId())

  // Splat pose from the store.
  const p = tune.environment.pose
  const splatPosition = [p.px, p.py, p.pz]
  const splatRotation = [p.rx, p.ry, p.rz]
  const splatScale    = p.scale

  // Avatar floor plane. An explicit override (per-asset default_pose.floorY,
  // tunable live) pins the character's feet so centered/offset splat exports
  // don't bury her under the floor; otherwise fall back to the splat-detected
  // floor. Without an env (null floorY + no detection) avatarFloor stays 0.
  const floorOverride = Number.isFinite(p.floorY) ? p.floorY : null
  useEffect(() => {
    const fy = floorOverride != null ? floorOverride : floorY
    if (fy != null) avatarFloor.setY(fy)
  }, [floorOverride, floorY])

  // Reset the detected floor whenever the active splat changes, so a stale
  // floor from a previous environment doesn't leave the camera/avatar shifted
  // after a swap or removal.
  useEffect(() => { setFloorY(null) }, [activeSplatUrl])

  // The whole avatar "stage" — floor, camera target, camera height, cage and
  // the lighting probe — is anchored to this Y: the height the avatar stands
  // at (explicit override, else detected splat floor, else 0). Anchoring the
  // camera + cage + probe here means a splat whose floor sits at y=-6 shifts
  // the entire rig down together (framing the avatar) instead of the camera
  // staring at a fixed y=1.35 while she stands 6 m below. No environment and
  // floor-at-0 splats (studio/Messy) resolve to 0 → unchanged.
  const stageFloorY = floorOverride != null ? floorOverride : (floorY ?? 0)
  const headProbe = [0, AVATAR_HEAD_PROBE[1] + stageFloorY, 0]
  // What the editor camera looks at, published by whatever loaded the content
  // (viewportFraming). Identity changes only when the content re-frames, so
  // R3F re-applies the target prop then and not on every reconcile — a fresh
  // array each render would undo the user's pan (rotate/zoom don't touch the
  // target, so those survived). Null until something is loaded: the prop is
  // omitted rather than aimed at the origin, which is the floor.
  const camTarget = useSyncExternalStore(subscribeContentTarget, getContentTarget, getContentTarget)

  // Shadow Y: explicit override wins; otherwise the stage floor.
  const resolvedShadowY = (tune.shadow.floorYOverride ?? stageFloorY) + 0.005
  // Backdrop ground sits at that same floor, i.e. just UNDER the shadow catcher
  // — so the catcher's transparent ShadowMaterial composites onto it instead of
  // z-fighting with it.
  const backdropGroundY = resolvedShadowY - 0.005
  // Stable identity: an inline ref callback would be a new function every
  // render, so React would detach (null) and re-attach the mesh on every
  // unrelated tune edit — leaving __BACKDROP__.ground transiently null.
  const backdropGroundRef = React.useCallback((node) => {
    const g = backdropGlobal()
    if (g) g.ground = node
  }, [])

  // ENG-812: apply the Tune Panel's global PBR/skin multipliers (roughness,
  // metalness, normalScale, thickness) onto the live materials. Before this the
  // sliders — including Normal Scale — wrote to the store but had no consumer,
  // so normal maps looked unresponsive to lighting. Object identity of these
  // slices is stable across unrelated tune edits (deepMerge only clones the
  // patched branch), so this only fires when pbr/pbrSkin actually change.
  const { pbr, pbrSkin } = tune
  useEffect(() => {
    setMaterialTuning({ pbr, pbrSkin })
  }, [pbr, pbrSkin])
  // Expose the camera controls on window for the offline bake tool (it sets .enabled=false
  // during capture so its lookAt() calls aren't overwritten every frame), the OGS eye
  // branch's 'change' subscription, App.frameAvatar, StreamAvatarModel and the cage gizmo.
  //
  // When the RIG drives (live mode with a camera rig in the blueprint) this is
  // cameraRigCompat, which speaks the same contract backed by the rig. Otherwise — edit
  // mode, or live's editor-camera fallback for a rig-less blueprint — it's the real drei
  // OrbitControls. The identity therefore swaps on a mode flip (and on a rig
  // appearing/disappearing in the live graph, which is the same transition), so a listener
  // bound to it ONCE is a bug waiting to happen: it keeps pointing at the object that was
  // published at subscribe time, and nothing dispatches on that any more. It cost the OGS
  // appr recolor — bound at pipeline-ready to the drei controls, dead the moment the rig
  // took the camera, so live mode rendered a splat whose colour never followed the view.
  // View-dependent consumers therefore watch the camera per frame (src/ogs/cameraWatch.ts)
  // and treat 'change' as a cheap extra. The hazard predates the rig (controlsRef.current
  // already changed identity), but the rig makes it routine, so the swap is done in one
  // place and covered by camera-compat.spec.js.
  useEffect(() => {
    if (typeof window === 'undefined') return
    const obj = rigDriven ? cameraRigCompat : controlsRef.current
    window.__ORBIT_CONTROLS__ = obj
    return () => {
      if (window.__ORBIT_CONTROLS__ === obj) {
        delete window.__ORBIT_CONTROLS__
      }
    }
  }, [controlsRef.current, rigDriven]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      {/* Studio rig (em_* — historically "edit mode"). In the one merged mode
         this is the NO-ENVIRONMENT default lighting: fixed and independent of
         the live-tune knobs, so a character with no environment applied is
         never a black silhouette (the env-derived ambient below defaults to
         0). An active environment stands it down — its IBL/probe stack owns
         the light there, and this also keeps em_key from re-introducing a
         stray second shadow on the ContactShadow catcher plane.
         The torch STANDS THIS DOWN when enabled (same rule as backdrop's ground
         plane vs the hardcoded edit floor): a light you placed has to be the
         actual light source, or these three fixed ones keep lighting the subject
         and the movable one is a rounding error on top of them — which is
         exactly what a probe for "is this being reflected?" must not be. */}
      {!hasEnv && !tune.torch.enabled && (
        <>
          <ambientLight name="em_ambient" color={0xffffff} intensity={0.8} />
          <directionalLight
            name="em_key"
            position={[3, 4, 2]}
            intensity={1.2}
            castShadow
            shadow-mapSize-width={1024}
            shadow-mapSize-height={1024}
            shadow-camera-far={20}
            shadow-camera-left={-3}
            shadow-camera-right={3}
            shadow-camera-top={4}
            shadow-camera-bottom={-1}
          />
          <directionalLight name="em_fill" args={[0xffffff]} position={[-2, 2, 3]} intensity={0.6} />
        </>
      )}

      {/* Movable torch. Mounted in BOTH modes, deliberately outside the `live`
         split: it's a look/lighting probe and the editor viewport is where it
         gets dragged around, so gating it on live would make it silently do
         nothing exactly where it's used — the same trap backdrop hit. Opt-in, so
         it changes nobody's default scene. */}
      <TorchLight torch={tune.torch} />

      {/* Environment pre-IBL fallback: tune-controlled ambient (default 0).
         Dropped once EnvProbe installs an envMap so we don't double-count;
         only mounted while an environment is active (the studio rig above
         covers the no-env case). */}
      {hasEnv && !iblApplied && (
        <ambientLight color={tune.lighting.ambientColor} intensity={tune.lighting.ambientIntensity} />
      )}

      {/* Live key light (ENG-812)
         Once EnvProbe installs the IBL we drop the fallback rig above, but a
         soft equirect IBL alone gives tangent-space normal detail almost no
         directional gradient to catch — pores/wrinkles/weave read flat. This
         single analytic directional restores that high-frequency term (IBL now
         supplies fill; this supplies form). Its direction reuses the shadow
         "sun" controls so shading and the contact shadow share one source of
         truth. lighting.keyLightIntensity = 0 → pure-IBL (pre-ENG-812) look. */}
      {iblApplied && tune.lighting.keyLightIntensity > 0 && (
        <directionalLight
          position={sphericalToXYZ(
            tune.shadow.lightDistance,
            tune.shadow.lightElevation,
            tune.shadow.lightAzimuth,
            [0, 0, 0],
          )}
          intensity={tune.lighting.keyLightIntensity}
        />
      )}

      {/* Live spotlights on orbit rigs (tune.spotlight/spotlight2/spotlight3),
         up to 3 (LiveTunePanel's LIGHTING — SPOT "+ ADD SPOT"). Each
         group yaws about Y at world origin; the light sits at local
         [0, height, distance] so it orbits the Y axis, and always aims at its
         own target at world [0, targetHeight, 0]. distance is clamped >= 0
         (negative would swing the light through the origin to the far side).
         OFF by default. Slot 1 keeps its original globals
         (window.__SPOTLIGHT__ / __SPOTLIGHT_TARGET__, locked by
         spotlight.spec.js); slots 2/3 are additive new globals. */}
      <SpotlightRig tune={tune.spotlight} lightGlobal="__SPOTLIGHT__" targetGlobal="__SPOTLIGHT_TARGET__" />
      <SpotlightRig tune={tune.spotlight2} lightGlobal="__SPOTLIGHT_2__" targetGlobal="__SPOTLIGHT_TARGET_2__" />
      <SpotlightRig tune={tune.spotlight3} lightGlobal="__SPOTLIGHT_3__" targetGlobal="__SPOTLIGHT_TARGET_3__" />

      {!hasEnv && (
        <>
          {/* Ground / Floor with Grid Pattern — the studio stage, stood down
             whenever an environment supplies its own floor.
             Stood down when the BACKDROP ground is on: both are floors at y≈0,
             so rendering both would z-fight, and the one the user explicitly
             enabled and picked a color for wins. Stood down for the backdrop
             SKY too, for a related reason with a different mechanism: this
             floor's color is hardcoded below and reads as black in an unlit
             scene, so it survived behind a picked background color and made the
             user set the background twice. See studioStageVisibility.js. */}
          {studioStage && (
            <mesh position={[0, 0, 0]} rotation={[-Math.PI / 2, 0, 0]} receiveShadow>
              <planeGeometry args={[400, 400]} />
              <meshStandardMaterial color={0x1a1a2e} roughness={0.8} metalness={0.2} />
            </mesh>
          )}
          {/* Floor Grid Reference — stands down with the studio floor above, for
             the same reason: the backdrop ground is a color the USER picked, and
             the grid draws at y=0.01, ON TOP of it. While this block was
             edit-mode-only the grid could stay (authoring chrome in an authoring
             view); Wave 2B mounts it whenever there is no environment, so a
             standalone-viewer boot rendered grid lines over the picked ground —
             backdrop-pixels.spec.js measured the 50/50 blend (2ec25f ground +
             666666 line = the observed 4a9462; hexes written without the '#'
             because the palette lock counts hex literals in comments). */}
          {studioStage && (
            <gridHelper args={[10, 20, 0x666666, 0x333333]} position={[0, 0.01, 0]} />
          )}
        </>
      )}

      {/* BACKDROP (ENG-894) — sky = scene.background, ground = an opaque plane at
         the stage floor. Deliberately OUTSIDE the live/edit split: it's a viewport
         LOOK control, and the editor viewport is where it gets authored, so gating
         it on live mode made it silently do nothing exactly where it was being
         used. (The `live`-only rule next door exists to stop live LIGHTING knobs
         from darkening the authoring view; this is opt-in and off by default, so
         it can't surprise anyone who hasn't enabled it.)
         Both halves self-gate on their own enabled flag; see DEFAULTS.backdrop. */}
      {tune.backdrop.skyEnabled && <SceneBackdrop color={tune.backdrop.skyColor} />}
      {tune.backdrop.groundEnabled && (
        <mesh
          ref={backdropGroundRef}
          position={[0, backdropGroundY, 0]}
          rotation={[-Math.PI / 2, 0, 0]}
          // Deliberately NOT receiveShadow: ShadowCatcher (just above this
          // plane) is the one shadow receiver and composites over whatever
          // is beneath it. Receiving here as well would darken the contact
          // shadow twice.
        >
          <planeGeometry args={[tune.backdrop.groundSize, tune.backdrop.groundSize]} />
          {/* UNLIT and UN-TONE-MAPPED on purpose — both are required for
             "the swatch is what you see", and each was a measured bug:
               • MeshStandardMaterial renders BLACK in the common case,
                 because ambientIntensity and shadow.lightIntensity both
                 default to 0 and the analytic key light only mounts once an
                 IBL applies — a scene with no baked environment has zero
                 light and swallows the color entirely.
               • toneMapped defaults to true, and R3F sets ACESFilmic on the
                 renderer, so the color shifts on the way out (measured: a
                 picked 2ec25f rendered as 5ec973 — written without the '#'
                 because the palette lock counts hex literals in comments
                 too). The sky can't drift the same way — a background clear
                 never goes through a fragment shader — so leaving this on
                 made the two halves of one control disagree with each other
                 AND with the swatch.
             The contact shadow still lands: ShadowCatcher is a separate
             transparent plane above this one. Locked by
             tests/e2e/ci/backdrop-pixels.spec.js, which reads the real
             framebuffer. */}
          <meshBasicMaterial color={tune.backdrop.groundColor} toneMapped={false} />
        </mesh>
      )}

      {/* Environment stack — always mounted (one mode): every piece self-gates
         on activeSplatUrl / its own enable flag, and a missing bake reports a
         loud no-cache state rather than faking lighting. */}
      {(
        <>
          {/* BACKDROP → "hide environment" unmounts the VISUAL splat only. The
             lighting derived from the same environment (EnvProbe, LightProbeVolume,
             ReflectionCaptureVolume below) keeps its `activeSplatUrl` and stays
             mounted, so hiding the scan doesn't relight the avatar. Unmount rather
             than `visible={false}`: Spark's SparkRenderer collects SplatMeshes
             itself, so a three-level visibility flag is not a contract we own —
             not rendering it at all is. Re-showing re-parses from the splat cache. */}
          {!tune.backdrop.hideEnvironment && (
            <SplatEnvironment
              key={activeSplatUrl}
              url={activeSplatUrl}
              version={tune.environment.splatVersion}
              position={splatPosition}
              rotation={splatRotation}
              scale={splatScale}
              onFloorY={(y) => { setFloorY(y); if (typeof window !== 'undefined') window.__ENV_FLOOR_Y__ = y }}
            />
          )}
          <EnvProbe
            splatUrl={activeSplatUrl}
            probePosition={headProbe}
            onApplied={setIblApplied}
          />
          <LightProbeVolume splatUrl={activeSplatUrl} splatVersion={tune.environment.splatVersion} reloadKey={bakeReloadKey} />
          <ReflectionCaptureVolume splatUrl={activeSplatUrl} splatVersion={tune.environment.splatVersion} reloadKey={bakeReloadKey} />
          {/* Keyboard-drivable reference rig: matte ball (diffuse LightProbe)
             + chrome mirror ball (specular reflection capture) in one moving
             group. Arrow keys / PageUp-Down drive window.__AVATAR_PROBE_POS__
             so both bakes sample at the rig's location — drive it to test
             diffuse + reflections together. userData.noEnvBake keeps both
             balls out of the offline cubemap/grid bake. */}
          {(tune.debug.mirrorBall || tune.debug.probeSphere) && (
            <ProbeReferenceSphere
              showMatte={tune.debug.probeSphere}
              showMirror={tune.debug.mirrorBall}
            />
          )}
          {/* Directional shadow-casting sun — mounted only while its own toggle
             is on, so toggling it removes ONLY its shadow from the shared
             catcher (not the plane itself). Also gated on hasEnv: the contact
             shadow is an environment-look feature (its floorY tracks the splat
             floor), and its 2048² VSM pass is the single most expensive thing
             in the frame under software rendering — in the no-env studio the
             em_key light already casts the authoring shadow, so mounting BOTH
             here starved the main thread on every no-env boot (measured:
             __AVATAR_READY__ blew CI's 45s budget). */}
          {hasEnv && tune.shadow.enabled && (
            <ShadowSunLight
              lightPos={sphericalToXYZ(
                tune.shadow.lightDistance,
                tune.shadow.lightElevation,
                tune.shadow.lightAzimuth,
                headProbe,
              )}
              intensity={tune.shadow.lightIntensity}
              color={kelvinToHex(tune.shadow.temperature)}
              mapSize={tune.shadow.mapSize}
              cameraSize={tune.shadow.cameraSize}
              radius={tune.shadow.radius}
              blurSamples={tune.shadow.blurSamples}
              bias={tune.shadow.bias}
            />
          )}
          {/* Single shared catcher plane for ALL casters (sun + spotlights).
             Present whenever some light is casting and the plane isn't fully
             transparent; opacity is the scene-level "Shadow" control.
             shadowEnabled mirrors the sun's own hasEnv gate above — with no
             environment the sun isn't mounted, so the catcher only serves an
             enabled spotlight there. spotlightEnabled is ANY of the up-to-3
             spotlight slots. */}
          {shouldShowShadowCatcher({
            shadowEnabled: hasEnv && tune.shadow.enabled,
            shadowOpacity: tune.shadow.opacity,
            spotlightEnabled: tune.spotlight.enabled || tune.spotlight2.enabled || tune.spotlight3.enabled,
          }) && (
            <ShadowCatcher floorY={resolvedShadowY} opacity={tune.shadow.opacity} />
          )}
          {tune.hemisphere.enabled && (
            <hemisphereLight
              color={scaledColor(tune.hemisphere.skyColor, tune.hemisphere.skyIntensity)}
              groundColor={scaledColor(tune.hemisphere.groundColor, tune.hemisphere.groundIntensity)}
            />
          )}
        </>
      )}

      {/* While an in-world OGS-stream is active (streamChar), the streamed avatar
          provides the whole appearance INCLUDING hair — suppress the host's local
          hair splat so it doesn't render a second, mismatched hair on top of it
          (the GLB/on-device avatars are already gated off in App.jsx). */}
      {!streamChar && <GSSHairLocalMount />}

      {/* Manual HDRI override (rendered when the user picks one in the
          HDRISwitcher UI). Takes precedence over EnvProbe's splat-derived
          env; harmless no-op when no override is set. */}
      <HDRIApplier />

      {/* NavMesh + movement (ENG-592). The ?nav=1 overlay and
          __MOVEMENT__/__NAV_DEV__ work with or without a baked navmesh: with
          an environment it fetches the splat's baked navmesh.json; without
          one use window.__NAV_DEV__.loadFlat() to inject a test grid. */}
      <NavMeshVolume
        splatUrl={activeSplatUrl ?? undefined}
        splatVersion={tune.environment.splatVersion}
        reloadKey={bakeReloadKey}
        /* Outliner selection draws the graph, alongside the Build panel's own
           toggle — one overlay, two ways to ask for it (ENG-1050). */
        selected={navmeshSelected}
      />
      {/* Granular voxel-surface cubes (the splat-transform voxel grid). */}
      <VoxelVolume
        splatUrl={activeSplatUrl ?? undefined}
        splatVersion={tune.environment.splatVersion}
        reloadKey={bakeReloadKey}
      />
      {/* The character's navigation capsule (green), tweakable in the Bake panel. */}
      <AgentCapsuleGizmo />

      {/* Waypoint picking for Generate Body Animation (click-to-place + markers). */}
      <WaypointPickerOverlay />

      {/* Viewport right-click menu + the "Walk here" route preview (ENG-1050).
          Mounted in BOTH modes: the menu is a viewport gesture, and its commands
          self-report why they're unavailable (no navmesh, no avatar) rather than
          the menu going silent in one mode. It stands down while the waypoint
          picker above is armed. */}
      <ViewportContextMenu />
      <WalkHereRoute />

      {/* CAMERA OWNERSHIP — exactly one driver of camera.position at a time.
          `rigDriven` (live mode AND the blueprint actually contains a camera
          rig) hands the camera to the blueprint rig via CameraControllerSync.
          Everything else — edit mode, or live with no rig authored — mounts
          the editor OrbitControls, the PlayCanvas/Unreal model: the editor
          camera is the default, a blueprint rig takes over the moment camera
          nodes exist (CameraRigNotice labels the fallback so it's never a
          silent substitution). Mounting both would give camera.position two
          writers fighting every frame; mounting neither is the old "inert
          camera" behaviour this fallback replaces. */}
      {!rigDriven && (
        <OrbitControls
          ref={controlsRef}
          mouseButtons={EDITOR_MOUSE_BUTTONS}
          {...(camTarget ? { target: camTarget } : {})}
        />
      )}
      {/* Head-tracked parallax for the OrbitControls-owned views (edit mode +
          live's rig-less fallback) — see the component's comment. Mounted
          right after OrbitControls so its useFrame runs after drei's. */}
      {!rigDriven && <EditorHeadParallax controlsRef={controlsRef} />}

      <CameraFloorSync controlsRef={controlsRef} floorY={stageFloorY} live={rigDriven} />
      {/* Transform gizmo (ENG-895) — mounted in BOTH modes; it self-gates on
         sceneSelection having a selected object, which only the Outliner panel
         can set. Camera stand-down during a drag goes through
         __ORBIT_CONTROLS__.enabled, which is published for both modes above. */}
      <TransformGizmo />
      {/* Always mounted (one mode): the blueprint's pointer/wheel/key events
          fire while editing too — that's the "always playing" contract. When
          a rig-less blueprint leaves OrbitControls in charge, the input
          events still fire; they just have no camera nodes listening. */}
      <ViewportInput />
      {rigDriven && <CameraControllerSync stageFloorY={stageFloorY} headProbe={headProbe} />}
      <CameraCageGizmo headProbe={headProbe} />
      <EnvRotationSync rx={p.rx} ry={p.ry} rz={p.rz} />
    </>
  )
}
